import React from 'react';
import { Callout, Step, DocHeading, DocTable } from './components';

function PageHowToBuy() {
  return (
    <div>
      <p className="text-sm text-brand-400 font-medium mb-2">Get Started</p>
      <h1 className="font-display text-3xl font-bold text-white tracking-tight mb-3">How to Purchase</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        This guide walks you through buying a MortisEngine subscription — from choosing a plan
        to receiving your license key. The whole process is automated; no manual steps are
        required after payment.
      </p>

      <DocHeading>Available Payment Methods</DocHeading>
      <DocTable
        headers={['Method', 'Processor', 'Extra fee', 'Delivery time']}
        rows={[
          ['Crypto — Litecoin (LTC)', 'Direct on-chain', 'None', '~15–30 min (3 confirmations)'],
          ['Card (Visa / Mastercard)', 'Whop.com', 'None', 'Instant after payment'],
        ]}
      />
      <Callout type="info">
        Both payment methods cost exactly the same: the price shown on the product page is
        the price you pay. Crypto is confirmed on-chain, card payments are instant.
      </Callout>

      <DocHeading>Step 1 — Register &amp; Log In</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        You need a MortisEngine account before purchasing. Registration is open — no invite code required.
      </p>
      <Step n={1} title="Create your account">
        Go to the <strong>Register</strong> page, choose a username and password, and submit the form.
      </Step>
      <Step n={2} title="Log in">
        Sign in with the credentials you just created. You will land on your Dashboard.
      </Step>

      <DocHeading>Step 2 — Choose a Plan</DocHeading>
      <Step n={1} title="Open the Shop">
        Click <strong>Shop</strong> in the top navigation bar. The available subscription plans
        are listed with their prices and durations.
      </Step>
      <Step n={2} title="Click on a plan">
        Select the plan you want to purchase. A payment dialog will open where you choose your
        payment method.
      </Step>
      <Step n={3} title="Select payment method and click Purchase">
        Pick <strong>Crypto</strong> or <strong>Card</strong> and click the{' '}
        <strong>Purchase</strong> button to proceed.
      </Step>

      <DocHeading>Paying with Crypto (Litecoin)</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        A payment modal opens with a unique Litecoin address and the exact LTC amount to send.
        The order is valid for <strong className="text-white">90 minutes</strong>.
      </p>
      <Step n={1} title="Copy the LTC address or scan the QR code">
        Send <strong className="text-white">exactly the displayed LTC amount</strong> to the
        shown address from any wallet or exchange (e.g. Binance). The QR code contains the
        plain address and is compatible with all major wallets.
      </Step>
      <Step n={2} title="Wait for confirmations">
        After your transaction is broadcast, the network needs{' '}
        <strong className="text-white">3 confirmations</strong> before the payment is accepted.
        This typically takes <strong className="text-white">15–30 minutes</strong> depending on
        network congestion. You can leave the page — the system tracks confirmations in the
        background.
      </Step>
      <Step n={3} title="Key is delivered automatically">
        Once 3 confirmations are reached, your order is marked as{' '}
        <strong className="text-white">Delivered</strong> and the license key appears in your
        Dashboard under the <strong>Orders</strong> tab. No action is needed from you.
      </Step>
      <Callout type="warning">
        Send the <strong>exact amount shown</strong> — not more, not less. The system matches
        incoming transactions by amount. Do not reuse the address for a second payment; always
        start a new order instead.
      </Callout>
      <Callout type="danger">
        If you do not send the payment within 90 minutes, the order expires. You can simply
        click Purchase again to generate a new order with a fresh address.
      </Callout>

      <DocHeading>Paying with Card</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        Card payments are processed by <strong className="text-white">Whop.com</strong>, a trusted
        third-party payment processor — but you never leave this site.
      </p>
      <Step n={1} title="Complete payment here">
        After clicking Purchase, a secure checkout opens directly on this page with your order
        pre-filled. Enter your card details and confirm.
      </Step>
      <Step n={2} title="Payment is confirmed automatically">
        After a successful payment, our system is notified automatically within seconds. No
        waiting for confirmations.
      </Step>
      <Step n={3} title="Key is delivered instantly">
        Your license key appears right in the checkout window, and your order status changes to
        <strong className="text-white"> Delivered</strong> in your Dashboard under <strong>Orders</strong>.
      </Step>
      <Callout type="info">
        The card fields are hosted by Whop.com inside a secure frame — we never see or store your
        card details.
      </Callout>

      <DocHeading>Step 3 — Find Your Key</DocHeading>
      <Step n={1} title="Open the Dashboard">
        Navigate to <strong>Dashboard</strong> (the <code className="code-inline">/panel</code> page).
      </Step>
      <Step n={2} title="Go to the Orders tab">
        Click the <strong>Orders</strong> tab. Your most recent order will be listed at the top.
        When the status is <strong className="text-white">Delivered</strong>, click the order
        row to reveal your license key.
      </Step>
      <Step n={3} title="Copy the key">
        Click the key to copy it to your clipboard. Keep it safe — you will need it in the next
        step.
      </Step>

      <Callout type="success">
        Once you have your key, head to the{' '}
        <strong>Activating Your Key</strong> page to bind it to your account.
      </Callout>

      <DocHeading>Refunds</DocHeading>
      <p className="text-sm text-gray-400">
        Refunds are only considered if the request is made within <strong className="text-white">14 days</strong>{' '}
        of purchase <strong className="text-white">and</strong> the key has not been activated
        or the software has not been downloaded or used. See the{' '}
        <a href="/terms" className="text-brand-400 hover:text-brand-300 underline" target="_blank" rel="noopener noreferrer">
          Terms of Service
        </a>{' '}
        for the full policy.
      </p>
    </div>
  );
}

export default PageHowToBuy;
